import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { adminApi, type AdminUser } from '../api/admin';

function formatDate(value: string | null) {
  return value ? new Date(value).toLocaleDateString('en-GB') : '—';
}

export default function UsersPanel() {
  const queryClient = useQueryClient();
  const [query, setQuery] = useState('');
  const [submitted, setSubmitted] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<AdminUser | null>(null);

  const { data, isPending } = useQuery({
    queryKey: ['users', submitted, page],
    queryFn: () => adminApi.users(submitted, page),
  });

  const toggle = useMutation({
    mutationFn: ({ id, disabled }: { id: string; disabled: boolean }) =>
      adminApi.setDisabled(id, disabled),
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['users'] });
      if (selected?.id === variables.id) {
        setSelected(await adminApi.user(variables.id));
      }
    },
  });

  return (
    <>
      <section className="card">
        <form
          className="toolbar"
          onSubmit={(e) => {
            e.preventDefault();
            setPage(1);
            setSubmitted(query);
          }}
        >
          <input
            className="field__input"
            placeholder="Email, name or BULSTAT"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <button className="btn btn--primary" type="submit">
            Search
          </button>
        </form>
        {/* Searching by ЕГН is deliberately not offered: the column is encrypted, so it could not
            match, and the field would invite pasting a national ID into a search box. */}
        <p className="muted">Identification numbers are not searchable.</p>
      </section>

      <section className="card">
        {isPending && <p className="muted">Loading…</p>}

        {data && (
          <>
            <div className="table-scroll">
              <table className="table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Tier</th>
                    <th>Effective</th>
                    <th>Created</th>
                    <th>Came from</th>
                    <th>Last seen</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((user) => (
                    <tr key={user.id} className={user.isDisabled ? 'is-disabled' : undefined}>
                      <td>
                        {user.email}
                        {user.isAdmin && <span className="badge">admin</span>}
                        {user.isDisabled && <span className="badge badge--danger">disabled</span>}
                      </td>
                      <td>{user.tier}</td>
                      <td>{user.effectiveTier}</td>
                      <td>{formatDate(user.createdAt)}</td>
                      {/* Campaign parameters from the visit that led to the sign-up. Present only when
                          the visitor agreed to analytics, so a dash means "did not say", not "direct". */}
                      <td className="muted">{user.cameFrom ?? '—'}</td>
                      <td>{formatDate(user.lastSeenAt)}</td>
                      <td>
                        <button
                          type="button"
                          className="btn btn--small"
                          onClick={() => setSelected(user)}
                        >
                          Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="toolbar">
              <button
                type="button"
                className="btn"
                disabled={!data.hasPrevious}
                onClick={() => setPage(page - 1)}
              >
                ←
              </button>
              <span className="muted">
                {data.page} / {Math.max(1, data.totalPages)} · {data.totalCount} users
              </span>
              <button
                type="button"
                className="btn"
                disabled={!data.hasNext}
                onClick={() => setPage(page + 1)}
              >
                →
              </button>
            </div>
          </>
        )}
      </section>

      {selected && (
        <section className="card">
          <div className="topbar">
            <h2>{selected.email}</h2>
            <button type="button" className="btn" onClick={() => setSelected(null)}>
              Close
            </button>
          </div>

          <dl className="datalist">
            <dt>Name</dt>
            <dd>{selected.displayName ?? '—'}</dd>
            <dt>Language</dt>
            <dd>{selected.language}</dd>
            <dt>Email confirmed</dt>
            <dd>{selected.emailConfirmed ? 'yes' : 'no'}</dd>
            <dt>Onboarding</dt>
            <dd>{selected.onboardingCompleted ? 'complete' : 'incomplete'}</dd>
            <dt>Tier / effective</dt>
            <dd>
              {selected.tier} / {selected.effectiveTier}
            </dd>
            <dt>Subscription status</dt>
            <dd>{selected.subscriptionStatus ?? '—'}</dd>
            <dt>Period ends</dt>
            <dd>{formatDate(selected.currentPeriodEnd)}</dd>
            <dt>ЕГН</dt>
            <dd>{selected.egnLast4 ? `••••••${selected.egnLast4}` : '—'}</dd>
            <dt>BULSTAT</dt>
            <dd>{selected.bulstat ?? '—'}</dd>
            <dt>Income / obligations / declarations</dt>
            <dd>
              {selected.incomeEntryCount} / {selected.obligationCount} / {selected.declarationCount}
            </dd>
          </dl>

          <button
            type="button"
            className={selected.isDisabled ? 'btn btn--primary' : 'btn'}
            disabled={toggle.isPending}
            onClick={() => toggle.mutate({ id: selected.id, disabled: !selected.isDisabled })}
          >
            {selected.isDisabled ? 'Re-enable account' : 'Disable account'}
          </button>
          {/* Disabling rotates the security stamp, so a live session ends within minutes rather
              than lasting until the cookie expires. */}
          <p className="muted">Disabling ends any active session within about five minutes.</p>
        </section>
      )}
    </>
  );
}
